import React, { useContext, useEffect, useState } from "react";
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Badge, Spinner, HStack, VStack, Input, Select, Button, Checkbox, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, useToast } from "@chakra-ui/react";
import { Navigate } from "react-router-dom";
import AuthContext from "../../../contexts/Auth";
import client from "../../../helpers/Client";
import Pagination from "../../../components/pagination/Pagination";
import ExportButton from "../../../components/export/ExportButton";
import urlToId from "../../../helpers/UrlToId";

export default function AdminNotes() {
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [objectModel, setObjectModel] = useState("");
  const [objectId, setObjectId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [numOfPages, setNumOfPages] = useState(1);
  const [numOfItems, setNumOfItems] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const createDisclosure = useDisclosure();
  const editDisclosure = useDisclosure();
  const [editNote, setEditNote] = useState(null);
  const [form, setForm] = useState({ object_model: "", object_id: "", note: "" });
  const toast = useToast();

  // Hooks must be called unconditionally; guard inside effects instead of returning early

  const fetchNotes = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (objectModel) params.set("object_model", objectModel);
      if (objectId) params.set("object_id", objectId);
      params.set("page", page);
      const { data } = await client.get(`/notes/?${params.toString()}`);
      const items = data.results || data;
      setNotes(items);
      setNumOfPages(data.num_of_pages || data.total_pages || 1);
      setNumOfItems(data.num_of_items || data.count || items.length || 0);
      setCurrentPage(data.current_page || page);
    } catch (e) {
      toast({ title: "Notlar alınamadı", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperUser) return;
    fetchNotes(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperUser, objectModel, objectId, currentPage]);

  const handleCreate = async () => {
    try {
      await client.post("/notes/", { object_model: form.object_model, object_id: Number(form.object_id), note: form.note });
      toast({ title: "Not oluşturuldu", status: "success" });
      createDisclosure.onClose();
      setForm({ object_model: "", object_id: "", note: "" });
      fetchNotes(currentPage);
    } catch (error) {
      toast({ title: "Oluşturma başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const openEdit = (note) => {
    setEditNote(note);
    setForm({ object_model: note.object_model, object_id: note.object_id, note: note.note || "" });
    editDisclosure.onOpen();
  };

  const handleEdit = async () => {
    try {
      await client.put(`/notes/${urlToId(editNote.url)}/`, { object_model: form.object_model, object_id: Number(form.object_id), note: form.note });
      toast({ title: "Not güncellendi", status: "success" });
      editDisclosure.onClose();
      setEditNote(null);
      fetchNotes(currentPage);
    } catch (error) {
      toast({ title: "Güncelleme başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const handleDelete = async (note) => {
    if (!window.confirm("Seçilen notu silmek istediğinize emin misiniz?")) return;
    try {
      await client.post(`/notes/${urlToId(note.url)}/delete/`, { confirm: true });
      toast({ title: "Not silindi", status: "success" });
      fetchNotes(currentPage);
    } catch (error) {
      toast({ title: "Silme başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected).map(urlToId);
    if (ids.length === 0) return;
    try {
      await Promise.all(
        notes
          .filter((n) => ids.includes(urlToId(n.url)))
          .map((n) => client.post(`/notes/${urlToId(n.url)}/delete/`, { confirm: true }))
      );
      toast({ title: "Seçilen notlar silindi", status: "success" });
      setSelected(new Set());
      fetchNotes(currentPage);
    } catch (error) {
      toast({ title: "Toplu silme başarısız", status: "error" });
    }
  };

  if (!isSuperUser) {
    return <Navigate to="/ana-sayfa/" replace />;
  }

  const objectModelChoices = [
    { value: "customer.customer", label: "Müşteri" },
    { value: "ticket.ticket", label: "İş Emri" },
    { value: "ticket.task", label: "Görev" },
    { value: "ticket.servicerecord", label: "Servis Kaydı" },
  ];

  return (
    <Box p={6}>
      <Heading size="md" mb={4}>Not Yönetimi</Heading>

      <VStack align="stretch" spacing={4} mb={4}>
        <HStack spacing={3}>
          <Select placeholder="Model" value={objectModel} onChange={(e) => setObjectModel(e.target.value)} w="240px">
            {objectModelChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </Select>
          <Input placeholder="Nesne ID" value={objectId} onChange={(e) => setObjectId(e.target.value)} w="160px" />
          <Button colorScheme="blue" onClick={() => { setCurrentPage(1); fetchNotes(1); }}>Filtrele</Button>
          <Button colorScheme="green" onClick={createDisclosure.onOpen}>Yeni Not</Button>
          {selected.size > 0 && (
            <Button colorScheme="red" variant="outline" onClick={bulkDelete}>Seçilenleri Sil</Button>
          )}
          <ExportButton
            data={(notes || []).map(n => ({
              id: urlToId(n.url),
              model: n.object_model || "",
              nesne_id: n.object_id ?? "",
              not: n.note || "",
              olusturan: n.created_by_name || "",
              tarih: n.created_at || "",
            }))}
            filename="notlar"
            columns={[
              { key: "id", label: "ID" },
              { key: "model", label: "Model" },
              { key: "nesne_id", label: "Nesne ID" },
              { key: "not", label: "Not" },
              { key: "olusturan", label: "Oluşturan" },
              { key: "tarih", label: "Tarih" },
            ]}
            sheetName="Notlar"
            excelOptions={{ title: "Notlar" }}
          />
        </HStack>
      </VStack>

      {loading ? (
        <Spinner />
      ) : (
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th></Th>
              <Th>Model</Th>
              <Th>Nesne ID</Th>
              <Th>Not</Th>
              <Th>Oluşturan</Th>
              <Th>Tarih</Th>
              <Th>Aksiyon</Th>
            </Tr>
          </Thead>
          <Tbody>
            {notes.map((n) => {
              const isSelected = selected.has(n.url);
              return (
                <Tr key={n.url}>
                  <Td>
                    <Checkbox isChecked={isSelected} onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(n.url); else next.delete(n.url);
                      setSelected(next);
                    }} />
                  </Td>
                  <Td>{n.object_model}</Td>
                  <Td>{n.object_id}</Td>
                  <Td>{n.note?.slice(0,120) || '-'}</Td>
                  <Td><Badge>{n.created_by?.username || '-'}</Badge></Td>
                  <Td>{n.created_at || '-'}</Td>
                  <Td>
                    <Button size="xs" variant="outline" onClick={() => openEdit(n)}>Düzenle</Button>
                    <Button size="xs" colorScheme="red" variant="outline" ml={2} onClick={() => handleDelete(n)}>Sil</Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}

      <Box mt={4}>
        <Pagination path={"admin/notlar"} num_of_items={numOfItems} num_of_pages={numOfPages} currentPage={currentPage} onPageChange={(p) => fetchNotes(p)} />
      </Box>

      {/* Create Modal */}
      <Modal isOpen={createDisclosure.isOpen} onClose={createDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Not Oluştur</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Model</FormLabel>
                <Select value={form.object_model} onChange={(e) => setForm({ ...form, object_model: e.target.value })} placeholder="Seçiniz">
                  {objectModelChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Nesne ID</FormLabel>
                <Input value={form.object_id} onChange={(e) => setForm({ ...form, object_id: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Not</FormLabel>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={createDisclosure.onClose}>İptal</Button>
            <Button colorScheme="blue" onClick={handleCreate}>Oluştur</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editDisclosure.isOpen} onClose={editDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Notu Düzenle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Model</FormLabel>
                <Select value={form.object_model} onChange={(e) => setForm({ ...form, object_model: e.target.value })} placeholder="Seçiniz">
                  {objectModelChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Nesne ID</FormLabel>
                <Input value={form.object_id} onChange={(e) => setForm({ ...form, object_id: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Not</FormLabel>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={editDisclosure.onClose}>İptal</Button>
            <Button colorScheme="blue" onClick={handleEdit}>Kaydet</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}